/**
 * Converts LoadSEER adjustments to GeoJSON format for Kepler.gl visualization
 */

import type { AdjustmentDto } from '../types';
import type { UpstreamNodeInfo } from '../services/upstream-node-resolver';
import type { Feature, FeatureCollection, Point } from 'geojson';

/**
 * GeoJSON feature properties for adjustments
 */
export interface AdjustmentGeoJSONProperties {
  // Identity
  adjustmentId: number | null;
  source: string; // 'Spatial', 'User', or 'NewGrowth'

  // Classification
  customerClassName: string | null;
  adjustmentTypeName: string | null;

  // Load characteristics
  kwPerUnit: number | null;
  numberOfUnits: number;
  kw_total?: number; // Calculated: kwPerUnit × numberOfUnits
  powerFactor: number | null;
  phase: string | null;
  sign: number;

  // Spatial (extracted from Point geometry)
  longitude: number;
  latitude: number;

  // Temporal
  startDate: string | null;
  endDate: string | null;

  // Location context
  lsid: string | null;
  nodeName: string | null;
  isDownstream: boolean;

  // Metadata
  notes: string | null;
  isExcluded: boolean;

  // Upstream node (optional, from upstream discovery)
  upstreamNodeLsid?: string;
  upstreamNodeName?: string;
  upstreamNodeType?: string;
}

/**
 * Conversion statistics
 */
export interface ConversionStatistics {
  totalInput: number;
  converted: number;
  excludedNoLocation: number;
}

/**
 * Converts LoadSEER adjustment DTOs to GeoJSON FeatureCollection
 *
 * @param adjustments - Array of AdjustmentDto from API (may include UpstreamNodeInfo fields)
 * @returns GeoJSON FeatureCollection with Point features
 */
export function adjustmentsToGeoJSON(
  adjustments: Array<AdjustmentDto & Partial<UpstreamNodeInfo>>
): {
  features: FeatureCollection<Point, AdjustmentGeoJSONProperties>;
  statistics: ConversionStatistics;
} {
  const features: Feature<Point, AdjustmentGeoJSONProperties>[] = [];
  let excludedNoLocation = 0;

  for (const adjustment of adjustments) {
    // Filter out adjustments without valid location
    if (
      !adjustment.location ||
      typeof adjustment.location.x !== 'number' ||
      typeof adjustment.location.y !== 'number'
    ) {
      excludedNoLocation++;
      continue;
    }

    const feature: Feature<Point, AdjustmentGeoJSONProperties> = {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [adjustment.location.x, adjustment.location.y],
      },
      properties: {
        // Identity
        adjustmentId: adjustment.adjustmentId,
        source: adjustment.source,

        // Classification
        customerClassName: adjustment.customerClassName,
        adjustmentTypeName: adjustment.adjustmentTypeName,

        // Load characteristics
        kwPerUnit: adjustment.kwPerUnit,
        // kWhPerUnit removed per requirements
        numberOfUnits: adjustment.numberOfUnits,
        // Calculate kw_total if both values are valid
        ...(adjustment.kwPerUnit !== null &&
        adjustment.numberOfUnits > 0 &&
        adjustment.kwPerUnit > 0
          ? { kw_total: adjustment.kwPerUnit * adjustment.numberOfUnits }
          : {}),
        powerFactor: adjustment.powerFactor,
        phase: adjustment.phase,
        sign: adjustment.sign,

        // Spatial (extracted from Point geometry)
        longitude: adjustment.location.x,
        latitude: adjustment.location.y,

        // Temporal
        startDate: adjustment.startDate,
        endDate: adjustment.endDate,

        // Location context
        lsid: adjustment.lsid,
        nodeName: adjustment.nodeName,
        isDownstream: adjustment.isDownstream,

        // Metadata
        notes: adjustment.notes,
        isExcluded: adjustment.isExcluded,

        // Upstream node (if enriched)
        ...(adjustment.upstreamNodeLsid
          ? {
              upstreamNodeLsid: adjustment.upstreamNodeLsid,
              upstreamNodeName: adjustment.upstreamNodeName,
              upstreamNodeType: adjustment.upstreamNodeType,
            }
          : {}),
      },
    };

    features.push(feature);
  }

  const featureCollection: FeatureCollection<Point, AdjustmentGeoJSONProperties> = {
    type: 'FeatureCollection',
    features,
  };

  const statistics: ConversionStatistics = {
    totalInput: adjustments.length,
    converted: features.length,
    excludedNoLocation,
  };

  return { features: featureCollection, statistics };
}
